import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_TableOptions,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";

import {
  Box,
  Button,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  useGetRiskmap,
  useUpdateRiskmap,
  useDeleteRiskmap,
  useCreateRiskmap,
  useApplyRiskmap,
  useExportRiskmaps,
  useDeleteRiskmaps,
} from "../../../hooks/riskmap";
import { Riskmap } from "../../../types/riskmap";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ImportFromCSV from "../../csvimport/importFromCsv";
import { baseURL } from "../../../api/apiClient";

const RiskmapTable = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState<string | undefined>("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [columnFilters]);

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const openImportModal = () => setIsImportModalOpen(true);
  const closeImportModal = () => setIsImportModalOpen(false);

  const deleteRiskmaps = useDeleteRiskmaps(columnFilters, sorting, rowCount);

  const triggerDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete all currently filtered Riskmaps?"
    );
    if (confirmDelete) {
      try {
        await deleteRiskmaps();
      } catch (error) {
        console.error(error);
      }
    }
  };
  const { mutateAsync: deleteRiskmap } = useDeleteRiskmap();

  const {
    data: riskmapQueryResults = [],
    isError: isLoadingRiskmapError,
    isFetching: isFetchingRiskmap,
    isLoading: isLoadingRiskmap,
  } = useGetRiskmap({ pagination, columnFilters, sorting, globalFilter });

  const fetchedRiskmap = riskmapQueryResults[0]?.riskmap || [];

  useEffect(() => {
    const count = riskmapQueryResults[0]?.rowCount;
    if (count == 0) {
      setRowCount(0);
    }
    if (!count || count === rowCount) {
      return;
    }
    setRowCount(count);
  }, [riskmapQueryResults]);

  const handleSaveRiskmap: MRT_TableOptions<Riskmap>["onEditingRowSave"] =
    async ({ values, table }) => {
      const newValidationErrors = validateRiskmap(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await updateRiskmap(values);
    };

  const handleCreateRiskmap: MRT_TableOptions<Riskmap>["onCreatingRowSave"] =
    async ({ values, table }) => {
      const newValidationErrors = validateRiskmap(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await createRiskmap(values);
    };

  const openDeleteConfirmModal = (row: MRT_Row<Riskmap>) => {
    if (window.confirm("Are you sure you want to delete this Risk map?")) {
      deleteRiskmap(row.original.id);
    }
  };

  const exportRiskmaps = useExportRiskmaps(columnFilters, sorting);

  const handleExport = async () => {
    try {
      await exportRiskmaps();
    } catch (error) {
      toast.error("Failed to export");
    } finally {
    }
  };

  function validateRiskmap(riskmap: Riskmap) {
    const validateRequired = (value: any) => !!value;

    return {
      name: !validateRequired(riskmap.name) ? "Name is required" : "",
    };
  }

  const tableName = "riskmap_table";
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const tableState = JSON.parse(localStorage.getItem("tableState") || "{}");
    return (
      tableState[tableName]?.columnVisibility || {
        id: false,
      }
    );
  });

  useEffect(() => {
    localStorage.setItem(
      "tableState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("tableState") || "{}"),
        [tableName]: {
          columnVisibility,
        },
      })
    );
  }, [columnVisibility, tableName]);

  const columns = useMemo<MRT_ColumnDef<Riskmap>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        enableEditing: false,
        enableSorting: false,
        size: 80,
      },

      {
        accessorKey: "name",
        header: "Name",
        size: 220,
        enableEditing: (row) => !row.original.id,
        Cell: ({ row }) => {
          return (
            <>
              <Link to={`/riskmap/${row.original.name}`}>
                {row.original.name}
              </Link>
            </>
          );
        },
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              name: undefined,
            }),
        },
      },

      {
        accessorKey: "risk",
        header: "Risk",
        size: 40,
        enableSorting: true,
        enableEditing: true,
        enableColumnFilter: true,
      },
    ],
    [validationErrors]
  );
  const table = useMaterialReactTable({
    columns,
    enableGlobalFilter: false,
    data: fetchedRiskmap,
    initialState: {
      showColumnFilters: true,
      density: "compact",
      columnVisibility: columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    selectAllMode: "all",
    enableColumnResizing: true,
    getRowId: (row) => row.id,
    rowNumberDisplayMode: "original",
    muiToolbarAlertBannerProps: isLoadingRiskmapError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    muiTableContainerProps: {
      sx: {
        minHeight: "500px",
      },
    },
    renderBottomToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          New Riskmap
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            useApplyRiskmap();
          }}
        >
          Apply Riskmap
        </Button>
        <Button variant="contained" onClick={handleExport}>
          Export {rowCount} Rows
        </Button>
        <Button variant="contained" onClick={openImportModal}>
          Import
        </Button>
        <Modal
          open={isImportModalOpen}
          onClose={closeImportModal}
          id="modal"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              height: "90vh",
              width: "90vw",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "8px",
              overflow: "auto",
            }}
          >
            <Typography id="modal-title" variant="h6" component="h2">
              CSV Import
            </Typography>
            <hr />
            <Typography id="modal-description" sx={{ mt: 2 }}></Typography>
            <ImportFromCSV
              apiUrl={`${baseURL}/api/riskmap/import/`}
              importType="riskmaps"
              onClose={closeImportModal}
            />

            <Button onClick={closeImportModal} sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        </Modal>
        <Button variant="contained" color="error" onClick={triggerDelete}>
          Delete {rowCount} Rows
        </Button>
      </Box>
    ),
    onCreatingRowCancel: () => setValidationErrors({}),
    onEditingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateRiskmap,
    onEditingRowSave: handleSaveRiskmap,

    enableRowActions: true,
    renderRowActionMenuItems: ({ row, table }) => [
      <Box key={row.id}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>,
    ],
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      columnVisibility: columnVisibility,
      isLoading: isLoadingRiskmap,
      showAlertBanner: isLoadingRiskmapError,
      showProgressBars: isFetchingRiskmap,
    },
  });

  const { mutateAsync: updateRiskmap } = useUpdateRiskmap({ table });
  const { mutateAsync: createRiskmap } = useCreateRiskmap({ table });

  return <MaterialReactTable table={table} />;
};
export default RiskmapTable;
